import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Users, Copy, CheckCircle2, Share2, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

/**
 * SplitCostButton
 * ───────────────
 * Appears on a completed / paid order card. Tapping opens a mini popover
 * where the rider picks a split amount + optional note, hits Send, and
 * receives a shareable `SP-XXXXXX` link + native share sheet trigger.
 */
export default function SplitCostButton({ order }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(() => Math.round((order?.total || 0) / 2));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {split_code, share_path, amount, currency}

  const shareUrl = () => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return `${origin}${result?.share_path || ""}`;
  };

  const buildShareText = () =>
    t(
      "split.shareText",
      "Split our Drivons ride? Please chip in {{amount}} {{currency}}: {{url}}",
      {
        amount: result?.amount?.toFixed?.(0) || amount,
        currency: (result?.currency || order?.currency || "").toUpperCase(),
        url: shareUrl(),
      }
    );

  const submit = async () => {
    if (busy) return;
    const amt = Number(amount);
    if (!amt || amt <= 0) {
      toast.error(t("split.enterAmount", "Enter a valid amount"));
      return;
    }
    setBusy(true);
    try {
      const res = await api.post(`/orders/${order.order_id}/split-request`, {
        amount: amt,
        note: note || undefined,
      });
      setResult(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl());
      toast.success(t("split.copied", "Link copied"));
    } catch (e) {
      toast.error(t("split.copyFailed", "Could not copy"));
    }
  };

  const share = async () => {
    const text = buildShareText();
    try {
      if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
        await navigator.share({
          title: t("split.shareTitle", "Split our Drivons ride"),
          text,
          url: shareUrl(),
        });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success(t("split.linkCopied", "Invite copied"));
      }
    } catch (err) {
      if (err?.name !== "AbortError") toast.error(t("split.shareFailed", "Could not share"));
    }
  };

  if (!order || (order.payment_status !== "paid" && order.payment_status !== "cash_confirmed")) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-full border border-electric-teal/40 bg-electric-teal/10 hover:bg-electric-teal/20 transition text-electric-teal px-3 py-1.5 text-[11px] font-black"
        data-testid={`split-cost-btn-${order.order_id}`}
      >
        <Users size={11} strokeWidth={2.5} /> {t("split.button", "Split with a friend")}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-3 bg-black/70 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="split-cost-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-md rounded-2xl bg-obsidian border border-white/15 p-5 shadow-[0_16px_48px_-8px_rgba(0,0,0,0.9)]"
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-black text-electric-teal">{t("split.eyebrow", "Split cost")}</div>
                <h3 className="text-lg font-black text-white">{t("split.title", "Ask a friend to chip in")}</h3>
              </div>
              <button onClick={() => setOpen(false)} className="w-8 h-8 rounded-full hover:bg-white/5 flex items-center justify-center text-white/60">
                <X size={14} />
              </button>
            </div>

            {!result && (
              <>
                <p className="text-xs text-white/60 mb-4">
                  {t("split.helper", "Share a link — they can pay their share without an account. Trip total: {{total}} {{currency}}", {
                    total: (order.total || 0).toFixed(2),
                    currency: (order.currency || "").toUpperCase(),
                  })}
                </p>
                <label className="block mb-3">
                  <span className="text-[10px] uppercase font-black tracking-widest text-white/50">{t("split.amountLabel", "Amount")}</span>
                  <div className="mt-1 flex items-center gap-2 rounded-xl bg-black/40 border border-white/10 px-3 py-2.5">
                    <input
                      type="number"
                      inputMode="decimal"
                      min={1}
                      max={order.total}
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="flex-1 bg-transparent outline-none text-lg font-black text-white tabular-nums"
                      data-testid="split-amount-input"
                    />
                    <span className="text-xs text-white/50 uppercase font-black">{(order.currency || "").toUpperCase()}</span>
                  </div>
                  <div className="mt-1.5 flex gap-1.5">
                    {[0.25, 0.33, 0.5].map((frac) => {
                      const val = Math.round((order.total || 0) * frac);
                      return (
                        <button
                          key={frac}
                          type="button"
                          onClick={() => setAmount(val)}
                          className="rounded-full bg-white/5 hover:bg-white/10 text-[10px] font-black text-white/70 px-2.5 py-1"
                          data-testid={`split-preset-${Math.round(frac * 100)}`}
                        >
                          {Math.round(frac * 100)}%
                        </button>
                      );
                    })}
                  </div>
                </label>
                <label className="block mb-4">
                  <span className="text-[10px] uppercase font-black tracking-widest text-white/50">{t("split.noteLabel", "Note (optional)")}</span>
                  <input
                    type="text"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    maxLength={200}
                    placeholder={t("split.notePh", "e.g. Airport run 🙏")}
                    className="mt-1 w-full rounded-xl bg-black/40 border border-white/10 px-3 py-2.5 text-sm text-white outline-none focus:border-electric-teal"
                    data-testid="split-note-input"
                  />
                </label>
                <button
                  type="button"
                  onClick={submit}
                  disabled={busy}
                  className="w-full rounded-full bg-electric-teal text-obsidian px-4 py-3 text-sm font-black inline-flex items-center justify-center gap-2 hover:bg-electric-teal/90 disabled:opacity-50 transition"
                  data-testid="split-create-btn"
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Share2 size={14} strokeWidth={2.5} />}
                  {t("split.createBtn", "Generate share link")}
                </button>
              </>
            )}

            {result && (
              <>
                <div className="mt-1 rounded-xl border border-electric-teal/40 bg-electric-teal/[0.06] p-4">
                  <div className="text-[10px] uppercase tracking-widest font-black text-electric-teal">{t("split.codeLabel", "Split code")}</div>
                  <div className="mt-1 font-black text-white tabular-nums tracking-[0.15em] text-2xl select-all" data-testid="split-result-code">
                    {result.split_code}
                  </div>
                  <div className="mt-2 text-xs text-white/50 break-all" data-testid="split-result-url">{shareUrl()}</div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={copy}
                    className="rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white px-3 py-2.5 text-xs font-black inline-flex items-center justify-center gap-1.5"
                    data-testid="split-copy-btn"
                  >
                    <Copy size={12} /> {t("split.copyLink", "Copy link")}
                  </button>
                  <button
                    type="button"
                    onClick={share}
                    className="rounded-full bg-electric-teal text-obsidian px-3 py-2.5 text-xs font-black inline-flex items-center justify-center gap-1.5 hover:bg-electric-teal/90"
                    data-testid="split-share-btn"
                  >
                    <Share2 size={12} strokeWidth={2.5} /> {t("split.shareNow", "Share")}
                  </button>
                </div>
                <div className="mt-3 text-[11px] text-white/50 inline-flex items-center gap-1.5">
                  <CheckCircle2 size={11} className="text-electric-teal" /> {t("split.hint", "They'll see the amount when they open the link.")}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
